// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import ztable from 'ztable';

import PageHeader from '../../../PageHeader.jsx';
import ContentBox from '../../../ContentBox.jsx';

import InfantMeasurements from './InfantMeasurements.jsx';
import InfantLengthAgeChart from './InfantLengthAgeChart.jsx';
import InfantWeightAgeChart from './InfantWeightAgeChart.jsx';
import InfantHCAgeChart from './InfantHCAgeChart.jsx';
import InfantWeightLengthChart from './InfantWeightLengthChart.jsx';

import ageWeight from './constants/wtageinf';
import ageLength from './constants/lenageinf';
import ageHC from './constants/hcageinf';
import lengthWeight from './constants/wtleninf';

type Props = {
  dob: string,
  sex: string,
  infantMeasurements: Object[],
  requiredMeasurements: Object[],
  wellChildChecks: Object[],
  print: boolean,
}

class InfantPageComponent extends Component<Props> {
  getAge(dateString: string) {
    const dob = moment(this.props.dob);
    const date = moment(dateString);
    return date.diff(dob, 'months');
  }

  getPercentile(value: number, l: number, m: number, s: number) {
    if (!value || !l || !m || !s) {
      return null;
    }
    let zscore;
    if (l === 0) {
      zscore = Math.log(value / m) / s;
    } else {
      zscore = (((value / m) ** l) - 1) / (l * s);
    }
    const percentile = Math.round(ztable(zscore) * 100);
    return percentile;
  }

  isOverdue = item => (item.required && !item.overridden && moment().isAfter(item.dateDue) && !item.weight);

  render() {
    // key to index into js constants files for percentiles, default to male
    const sex = this.props.sex === 'F' ? 'female' : 'male';

    // combine all measurement sources
    let measurements = this.props.infantMeasurements.slice();

    // add required measurements
    for (let i = 0; i < this.props.requiredMeasurements.length; i++) {
      measurements.push({
        ...this.props.requiredMeasurements[i],
        dateDue: moment(this.props.dob).add(i, 'month').toISOString(),
        required: true,
        showDelete: false,
      });
    }

    if (this.props.print) {
      const now = moment().toISOString();
      measurements = measurements.filter(m => m.dateDue < now);
    }

    // add measurements from WCCs Birth-3 years if measurements exist
    for (let i = 0; i < this.props.wellChildChecks.length; i++) {
      const {
        date, notes, vitals, editors, editDates,
      } = this.props.wellChildChecks[i];
      const { weight, length, headCircumference } = vitals;
      if (weight || length || headCircumference) {
        measurements.push({
          date,
          weight,
          length,
          headCircumference,
          editors,
          editDates,
          notes,
          showDelete: false,
          showEdit: false,
        });
      }
    }

    measurements = measurements.map((item) => {
      const age = this.getAge(item.date);
      // if birth, use index 0. Max index is 36
      const ageIndex = item.date === this.props.dob ? 0 : Math.min(age + 1, 36);
      const weightL = ageWeight[sex].l[ageIndex];
      const weightM = ageWeight[sex].m[ageIndex];
      const weightS = ageWeight[sex].s[ageIndex];
      const lengthL = ageLength[sex].l[ageIndex];
      const lengthM = ageLength[sex].m[ageIndex];
      const lengthS = ageLength[sex].s[ageIndex];
      const hcL = ageHC[sex].l[ageIndex];
      const hcM = ageHC[sex].m[ageIndex];
      const hcS = ageHC[sex].s[ageIndex];

      // Rounded to nearest 0.5 (e.g. 45.5, 46.5, etc), dummy length if DNE
      const roundedLength = item.length ? Math.floor(item.length) + 0.5 : 45;
      const maxIndex = lengthWeight[sex].l.length - 1;
      const lengthIndex = roundedLength === 45 ? 0 : Math.min(Math.max(roundedLength - 44.5, 0), maxIndex);
      const lwL = lengthWeight[sex].l[lengthIndex];
      const lwM = lengthWeight[sex].m[lengthIndex];
      const lwS = lengthWeight[sex].s[lengthIndex];

      return {
        age,
        weightPercentile: this.getPercentile(item.weight, weightL, weightM, weightS),
        lengthPercentile: this.getPercentile(item.length, lengthL, lengthM, lengthS),
        hcPercentile: this.getPercentile(item.headCircumference, hcL, hcM, hcS),
        lwPercentile: this.getPercentile(item.weight, lwL, lwM, lwS),
        ...item,
      };
    });

    measurements.sort((a, b) => {
      // overdues go first
      const aOverdue = this.isOverdue(a);
      const bOverdue = this.isOverdue(b);
      if (!aOverdue && bOverdue) {
        return 1;
      }
      if (!bOverdue && aOverdue) {
        return -1;
      }

      // otherwise based on date due or date if date due DNE
      let aDate = a.dateDue;
      if (!aDate && !a.overridden) {
        aDate = a.date;
      }
      let bDate = b.dateDue;
      if (!bDate && !b.overridden) {
        bDate = b.date;
      }
      if (!aDate) {
        return 1;
      }
      if (!bDate) {
        return -1;
      }
      if (aDate < bDate) {
        return 1;
      }
      if (bDate < aDate) {
        return -1;
      }
      return 0;
    });

    return (
      <div>
        <PageHeader title='Growth Charts (Birth-36 Months)' />
        <ContentBox title='Measurements' id='measurements'>
          <InfantMeasurements measurements={measurements} />
        </ContentBox>
        <ContentBox title='Weight-for-Age' id='weightAge' collapsable>
          <InfantWeightAgeChart measurements={measurements} sex={sex} />
        </ContentBox>
        <ContentBox title='Length-for-Age' id='lengthAge' collapsable>
          <InfantLengthAgeChart measurements={measurements} sex={sex} />
        </ContentBox>
        <ContentBox title='Head Circumference-for-Age' id='hcAge' collapsable>
          <InfantHCAgeChart measurements={measurements} sex={sex} />
        </ContentBox>
        <ContentBox title='Weight-for-Length' id='weightLength' collapsable>
          <InfantWeightLengthChart measurements={measurements} sex={sex} />
        </ContentBox>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  dob: state.patient.data.generalInfo.dob,
  sex: state.patient.data.generalInfo.sex,
  infantMeasurements: state.patient.data.infantMeasurements,
  requiredMeasurements: state.patient.data.requiredMeasurements,
  wellChildChecks: state.patient.data.wellChildChecks.slice(0, 9),
  print: state.patient.print,
});

const InfantPage = connect(
  mapStateToProps,
)(InfantPageComponent);

export default InfantPage;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/growthChart/infant/InfantPage.jsx